import React, { useState } from 'react';
import { Pill, Lock, User, ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // الربط مع قاعدة البيانات عبر السيرفر
      const res = await axios.post('https://my-pharmacy-api.onrender.com/api/login', { username, password });
      
      if (res.data.auth) {
        onLogin(true);
        localStorage.setItem('isAdmin', 'true');
        localStorage.setItem('adminUser', JSON.stringify(res.data.user));
      } else {
        setError(res.data.message || 'بيانات الدخول غير صحيحة');
      }
    } catch (err) {
      setError('عذراً، فشل الاتصال بالسيرفر.. تأكدي من تشغيله');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4" dir="rtl">
      
      {/* زخرفة خلفية بسيطة */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-100/50 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-md w-full bg-white/80 backdrop-blur-xl rounded-[50px] shadow-[0_20px_70px_rgba(0,0,0,0.05)] p-8 md:p-12 border border-white relative z-10">
        
        {/* أيقونة حماية علوية */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-blue-600 blur-2xl opacity-20 animate-pulse"></div>
            <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700 p-5 rounded-[28px] text-white shadow-xl shadow-blue-200">
              <ShieldCheck size={40} strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="text-center mb-10">
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">نظام صيدلية ميسون</h2>
          <p className="text-gray-400 font-bold mt-3 flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping"></span>
            بوابة الإدارة الآمنة
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* حقل اسم المستخدم */}
          <div className="space-y-2">
            <label className="text-xs font-black text-gray-400 mr-4 uppercase tracking-widest">المعرف الخاص</label>
            <div className="relative group">
              <User className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors" size={20} />
              <input 
                required
                type="text" 
                className="w-full bg-gray-50 border-2 border-transparent rounded-[24px] py-4.5 pr-14 pl-6 font-bold text-gray-700 focus:bg-white focus:border-blue-100 focus:ring-4 focus:ring-blue-50 transition-all outline-none"
                placeholder="اسم المستخدم"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
          </div>

          {/* حقل كلمة المرور */}
          <div className="space-y-2">
            <label className="text-xs font-black text-gray-400 mr-4 uppercase tracking-widest">كلمة السّر</label>
            <div className="relative group">
              <Lock className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors" size={20} />
              <input 
                required
                type={showPassword ? "text" : "password"} 
                className="w-full bg-gray-50 border-2 border-transparent rounded-[24px] py-4.5 pr-14 pl-14 font-bold text-gray-700 focus:bg-white focus:border-blue-100 focus:ring-4 focus:ring-blue-50 transition-all outline-none"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {/* زر العين لإظهار وإخفاء كلمة المرور */}
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors p-1"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* عرض الخطأ بأسلوب أنيق */}
          {error && (
            <div className="bg-red-50 text-red-500 p-4 rounded-2xl text-sm font-bold flex items-center gap-3 animate-head-shake">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              {error}
            </div>
          )}

          {/* زر الدخول */}
          <button 
            disabled={loading}
            type="submit" 
            className={`w-full py-5 rounded-[26px] font-black flex items-center justify-center gap-3 transition-all shadow-xl active:scale-95 mt-4 ${
              loading 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-gray-900 text-white hover:bg-blue-600 shadow-blue-100 hover:shadow-blue-200'
            }`}
          >
            {loading ? (
              <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <>ابدأ العمل الآن <ArrowRight size={20} /></>
            )}
          </button>
        </form>

        <p className="text-center text-[10px] text-gray-400 font-bold mt-10 uppercase tracking-[2px]">
          نظام ميسون التقني &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default Login;